import { z } from 'zod';
import {
    type NodeSocketCfg,
    baseNodeCfgSchema,
} from './base-node';
import type { NodeCfg } from '../nodes';
import { locationSchema } from '../../location.deployParam';
import { ProcessDataElementCollectionKindEnum, ProcessDataElementKindEnum } from '../../../../../db';

export const processCollectNodeCfgSchema = baseNodeCfgSchema.extend({
    kind: z.literal('processCollect'),
    controlValues: z.object({
        location: locationSchema,
        processId: z.string().uuid(),
        useStandardizedOutputs: z.object({
            status: z.boolean().default(true),
            specId: z.boolean().default(true),
            badCount: z.boolean().default(true),
            specName: z.boolean().default(true),
            goodCount: z.boolean().default(true),
            requiredCount: z.boolean().default(true),
            validationError: z.boolean().default(true),
        }),
        dataElements: z.array(z.object({
            name: z.string(),
            id: z.string().uuid(),
            sourceVarId: z.string(),
            collectionKind: z.enum([ ProcessDataElementCollectionKindEnum.Collect, ProcessDataElementCollectionKindEnum.SpecParam ]),
            type: z.enum([ ProcessDataElementKindEnum.String, ProcessDataElementKindEnum.Number, ProcessDataElementKindEnum.Pass, ProcessDataElementKindEnum.Material, ProcessDataElementKindEnum.MaterialModel, ProcessDataElementKindEnum.MaterialClass ]),
        })),
    }),
});
export interface ProcessCollectNodeCfgSchema extends z.infer<typeof processCollectNodeCfgSchema> {}

export const processCollectNodeSocketCfg = {
    controlSockets: {
        inputs: () => ({
            solve: 'event',
            collect: 'event',
        }),
        outputs: () => ({
            next: 'event',
        }),
    },
    dataSockets: {
        inputs: (cfg: NodeCfg<'processCollect'>): Record<'materialId' | string, 'string' | 'number' | 'boolean'> => {
            const ret: Record<'materialId' | string, 'string' | 'number' | 'boolean'> = {
                materialId: 'string',
            };
            for (const de of cfg.controlValues.dataElements.filter(de => de.collectionKind === ProcessDataElementCollectionKindEnum.Collect)) {
                if (de.type === ProcessDataElementKindEnum.String || de.type === ProcessDataElementKindEnum.Material || de.type === ProcessDataElementKindEnum.MaterialModel || de.type === ProcessDataElementKindEnum.MaterialClass) {
                    // FUTURE: if we add more types like uuid or even more specific material uuid then we can handle that here
                    ret[de.id] = 'string';
                } else if (de.type === ProcessDataElementKindEnum.Number) {
                    ret[de.id] = 'number';
                } else if (de.type === ProcessDataElementKindEnum.Pass) {
                    ret[de.id] = 'boolean';
                }
            }
            return ret;
        },
        outputs: (cfg: NodeCfg<'processCollect'>): Record<string, 'string' | 'number' | 'boolean'> => {
            const ret: Record<string, 'string' | 'number' | 'boolean'> = {};
            if (cfg.controlValues.useStandardizedOutputs.status) {
                ret.status = 'string';
            }
            if (cfg.controlValues.useStandardizedOutputs.specId) {
                ret.specId = 'string';
            }
            if (cfg.controlValues.useStandardizedOutputs.badCount) {
                ret.badCount = 'number';
            }
            if (cfg.controlValues.useStandardizedOutputs.specName) {
                ret.specName = 'string';
            }
            if (cfg.controlValues.useStandardizedOutputs.goodCount) {
                ret.goodCount = 'number';
            }
            if (cfg.controlValues.useStandardizedOutputs.requiredCount) {
                ret.requiredCount = 'number';
            }
            if (cfg.controlValues.useStandardizedOutputs.validationError) {
                ret.validationError = 'string';
            }
            for (const de of cfg.controlValues.dataElements.filter(de => de.collectionKind === ProcessDataElementCollectionKindEnum.SpecParam)) {
                if (de.type === ProcessDataElementKindEnum.Material) {
                    // material cannot be a spec param
                } else if (de.type === ProcessDataElementKindEnum.String) {
                    ret[de.id] = 'string';
                } else if (de.type === ProcessDataElementKindEnum.Number) {
                    ret[de.id] = 'number';
                } else if (de.type === ProcessDataElementKindEnum.Pass) {
                    ret[de.id] = 'boolean';
                }
            }
            return ret;
        },
        
    },
} satisfies NodeSocketCfg<ProcessCollectNodeCfgSchema>;
